import { useState, useCallback, useEffect } from "react";
import { Logo } from "./Logo";
import { Menu, X, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatformStatus } from "@/contexts/PlatformStatusContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { WishlistBadge } from "./WishlistBadge";
import { ShopifyCartDrawer } from "./ShopifyCartDrawer";
import { toast } from "sonner";
import { prefetchHandlers, prefetchRoute } from "@/lib/routePrefetch";

const navLinks = [
{ name: "home", href: "/" },
{ name: "Apparel", href: "/shop" },
{ name: "events", href: "/events" },
{ name: "Partners & Sponsors", href: "/partners" },
{ name: "blog", href: "/blog" }];


export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { maintenanceMode } = usePlatformStatus();
  const { isAdmin } = useAdminRole();
  const location = useLocation();
  const navigate = useNavigate();

  const isMaintenanceBlocked = maintenanceMode && !isAdmin;

  // Prefetch primary nav chunks during browser idle so the first
  // navigation feels instant even on mobile (no hover event).
  useEffect(() => {
    const idle =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 800));
    const handle = idle(() => {
      navLinks.forEach((l) => prefetchRoute(l.href));
      prefetchRoute(user ? '/dashboard' : '/auth');
    });
    return () => {
      const cancel = (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback;
      if (cancel && typeof handle === 'number') cancel(handle);
    };
  }, [user]);

  const handleNavClick = useCallback((e: React.MouseEvent, href: string) => {
    // During maintenance, block all navigation except /auth for non-admins
    if (isMaintenanceBlocked && href !== '/auth') {
      e.preventDefault();
      toast("Website is undergoing maintenance", {
        description: "Please check back shortly.",
      });
      return;
    }

    if (href.includes('#')) {
      const hash = href.split('#')[1];
      if (location.pathname === '/') {
        e.preventDefault();
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/' + '#' + hash);
        e.preventDefault();
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, [location.pathname, navigate, isMaintenanceBlocked]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30">
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Left Side */}
          <Link to="/" aria-label="Øutlaws home">
            <Logo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <Link
              key={link.name}
              to={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              {...prefetchHandlers(link.href)}
              className="nav-link capitalize font-serif text-base"
            >
                {link.name}
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {user ?
            <Link to="/dashboard" {...prefetchHandlers('/dashboard')} className="nav-link flex gap-2 items-center">
                <User className="w-4 h-4" />
                <span className="hidden md:inline">Dashboard</span>
              </Link> :

            <Link to="/auth" {...prefetchHandlers('/auth')} className="nav-link flex gap-2 items-center">
                <User className="w-4 h-4" />
                <span className="hidden md:inline">Log In / Register</span>
              </Link>
            }

            {/* Wishlist Icon */}
            <WishlistBadge />

            {/* Shopify Cart Drawer */}
            <ShopifyCartDrawer />

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="md:hidden mt-4 pb-4 border-t border-border/30 pt-4 animate-fade-in max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
            <Link
              key={link.name}
              to={link.href}
              className="nav-link text-lg capitalize"
              onClick={(e) => {
                handleNavClick(e, link.href);
                setMobileMenuOpen(false);
              }}>
              
                  {link.name}
                </Link>
            )}
              {user ?
            <Link
              to="/dashboard"
              className="nav-link text-lg flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}>
              
                  Dashboard
                  <User className="w-4 h-4" />
                </Link> :

            <Link
              to="/auth"
              className="nav-link text-lg flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}>
              
                  Log In / Register
                  <User className="w-4 h-4" />
                </Link>
            }
            </div>
          </div>
        }
      </nav>
    </header>);

};